/**
 * ScrollObserver - Modern replacement for Waypoints.js using Intersection Observer API
 * 
 * Provides full compatibility with Waypoints.js 4.0.1 API while using modern
 * browser APIs for better performance.
 * 
 * @class ScrollObserver
 */
export class ScrollObserver {
    /**
     * @type {ScrollObserver[]}
     * @static
     */
    static instances = [];

    /**
     * @type {boolean}
     * @static
     */
    static isSupported = typeof IntersectionObserver !== 'undefined';

    /**
     * @param {Object} options - Waypoints-compatible options
     * @param {HTMLElement} options.element - Element to observe
     * @param {Function} options.handler - Callback function(direction)
     * @param {number|string|Function} [options.offset=0] - Offset in pixels, string alias, or function
     * @param {boolean} [options.enabled=true] - Whether waypoint is enabled
     * @param {boolean} [options.continuous=true] - Whether to trigger on every scroll
     * @param {string} [options.group='default'] - Group name for waypoints
     * @param {HTMLElement|Window} [options.context=window] - Scroll context
     */
    constructor(options) {
        if (!options) {
            throw new Error('No options passed to ScrollObserver constructor');
        }
        if (!options.element) {
            throw new Error('No element option passed to ScrollObserver constructor');
        }
        if (!options.handler) {
            throw new Error('No handler option passed to ScrollObserver constructor');
        }

        if (!ScrollObserver.isSupported) {
            console.warn('IntersectionObserver not supported. Waypoints functionality will be limited.');
            return;
        }

        this.element = options.element;
        this.handler = options.handler;
        this.offset = options.offset || 0;
        this.enabled = options.enabled !== false;
        this.continuous = options.continuous !== false;
        this.group = options.group || 'default';
        this.context = options.context || window;

        // Internal state
        this.observer = null;
        this.direction = null;
        this.lastScrollTop = 0;
        this.lastScrollLeft = 0;
        this.isIntersecting = false;
        this.isInActiveZone = false; // Track if element is in active zone (25%-75% viewport)
        this.hasTriggered = false; // Track if waypoint has been triggered (for continuous: false)
        this.key = `waypoint-${ScrollObserver.instances.length}`;
        this.scrollHandler = null;

        // Initialize
        this._initObserver();
        ScrollObserver.instances.push(this);
    }

    /**
     * Initialize Intersection Observer
     * @private
     */
    _initObserver() {
        const rootMargin = this._calculateRootMargin();
        
        const observerOptions = {
            root: this.context === window ? null : this.context,
            rootMargin: rootMargin,
            // Use multiple thresholds to detect when element enters/leaves the intersection area
            // This ensures we can track the element's position continuously
            // Using many thresholds ensures we detect position changes even when element is still in viewport
            // For active zone mode (offset 0), we need fine-grained detection
            threshold: this.offset === 0 
                ? [0, 0.01, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 0.99, 1]
                : [0, 0.01, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.99, 1]
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                this._handleIntersection(entry);
            });
        }, observerOptions);

        this.observer.observe(this.element);
        
        // Track scroll position for direction detection
        this._initScrollTracking();
    }

    /**
     * Initialize scroll tracking for direction detection and active zone checking
     * @private
     */
    _initScrollTracking() {
        const scrollHandler = () => {
            const scrollTop = this.context === window 
                ? window.pageYOffset || document.documentElement.scrollTop
                : this.context.scrollTop;
            const scrollLeft = this.context === window
                ? window.pageXOffset || document.documentElement.scrollLeft
                : this.context.scrollLeft;

            this.lastScrollTop = scrollTop;
            this.lastScrollLeft = scrollLeft;

            // Continuously check if element is still in active zone
            // This ensures we catch cases where element leaves active zone but is still in viewport
            // Only check if offset is 0 (active zone mode) to avoid conflicts with other waypoints
            if (this.element && this.isIntersecting && this.offset === 0) {
                const rect = this.element.getBoundingClientRect();
                const viewportHeight = this.context === window
                    ? window.innerHeight
                    : this.context.clientHeight;
                
                const activeZoneTop = viewportHeight * 0.25;
                const activeZoneBottom = viewportHeight * 0.75;
                const activeZoneHeight = viewportHeight * 0.5;
                
                const elementTop = rect.top;
                const elementBottom = rect.bottom;
                const elementHeight = rect.height;
                
                const isCompletelyWithin = elementTop >= activeZoneTop && elementBottom <= activeZoneBottom;
                const isTallerThanActiveZone = elementHeight > activeZoneHeight;
                const exceedsAbove = elementTop < activeZoneTop;
                const exceedsBelow = elementBottom > activeZoneBottom;
                const fullyFills = isTallerThanActiveZone && exceedsAbove && exceedsBelow;
                
                const isInActiveZone = isCompletelyWithin || fullyFills;
                const wasInActiveZone = this.isInActiveZone;
                
                // CRITICAL: Always trigger handler when active zone status changes
                if (isInActiveZone !== wasInActiveZone) {
                    const direction = this._getDirection({ boundingClientRect: rect });
                    
                    // Update state BEFORE calling handler
                    this.isInActiveZone = isInActiveZone;
                    
                    if (this.handler.length > 1) {
                        this.handler.call(this, direction, isInActiveZone);
                    } else {
                        this.handler.call(this, direction);
                    }
                    this.direction = direction;
                }
            }
        };

        this.scrollHandler = scrollHandler;

        if (this.context === window) {
            window.addEventListener('scroll', scrollHandler, { passive: true });
        } else {
            this.context.addEventListener('scroll', scrollHandler, { passive: true });
        }
    }

    /**
     * Check if element is in active zone (25%-75% of viewport height)
     * Element is active if:
     * A: Completely within active zone (element top >= 25% AND element bottom <= 75%), OR
     * B: Larger than active zone and fully fills it (element height > 50% AND element top < 25% AND element bottom > 75%)
     * @param {IntersectionObserverEntry|Object} entry - Entry with boundingClientRect, or object with boundingClientRect
     * @returns {boolean}
     * @private
     */
    _isInActiveZone(entry) {
        const viewportHeight = this.context === window
            ? window.innerHeight
            : this.context.clientHeight;
        
        const activeZoneTop = viewportHeight * 0.25; // 25% from top
        const activeZoneBottom = viewportHeight * 0.75; // 75% from top
        const activeZoneHeight = viewportHeight * 0.5; // 50% height
        
        const rect = entry.boundingClientRect || entry;
        const elementTop = rect.top;
        const elementBottom = rect.bottom;
        const elementHeight = rect.height;
        
        // Case A: Element is completely within active zone
        // Element top >= 25% AND element bottom <= 75%
        const isCompletelyWithin = elementTop >= activeZoneTop && elementBottom <= activeZoneBottom;
        
        // Case B: Element is larger than active zone and fully fills it
        // Element height > 50% AND element top < 25% AND element bottom > 75%
        const isTallerThanActiveZone = elementHeight > activeZoneHeight;
        const exceedsAbove = elementTop < activeZoneTop;
        const exceedsBelow = elementBottom > activeZoneBottom;
        const fullyFills = isTallerThanActiveZone && exceedsAbove && exceedsBelow;
        
        return isCompletelyWithin || fullyFills;
    }

    /**
     * Handle intersection changes
     * @param {IntersectionObserverEntry} entry
     * @private
     */
    _handleIntersection(entry) {
        if (!this.enabled) return;

        const wasIntersecting = this.isIntersecting;
        this.isIntersecting = entry.isIntersecting;

        // Always check if element is in active zone (25%-75% viewport)
        // Check active zone whenever element is intersecting (boundingClientRect is available)
        let isInActiveZone = false;
        if (entry.isIntersecting && entry.boundingClientRect) {
            isInActiveZone = this._isInActiveZone(entry);
        }
        
        const wasInActiveZone = this.isInActiveZone;
        
        // CRITICAL: Always trigger handler when active zone status changes
        // This ensures explicit activation/deactivation per element
        if (isInActiveZone !== wasInActiveZone) {
            const direction = entry.isIntersecting ? this._getDirection(entry) : (this.direction || 'down');
            
            // Update state BEFORE calling handler
            this.isInActiveZone = isInActiveZone;
            
            // Call handler with direction and isInActiveZone
            // For backward compatibility, check if handler accepts second parameter
            if (this.handler.length > 1) {
                this.handler.call(this, direction, isInActiveZone);
            } else {
                // Old handler signature (only direction)
                this.handler.call(this, direction);
            }
            
            if (entry.isIntersecting) {
                this.direction = direction;
            }
            this.hasTriggered = true;
        } else if (entry.isIntersecting) {
            // Element is intersecting but active zone status hasn't changed
            // Update state and direction
            const direction = this._getDirection(entry);
            
            // For continuous mode, also trigger when in active zone
            if (this.continuous && isInActiveZone) {
                if (this.handler.length > 1) {
                    this.handler.call(this, direction, isInActiveZone);
                } else {
                    this.handler.call(this, direction);
                }
            }
            
            this.direction = direction;
            this.isInActiveZone = isInActiveZone;
        } else {
            // Element left intersection area (left viewport)
            // If was in active zone, deactivate
            if (wasInActiveZone) {
                if (this.handler.length > 1) {
                    this.handler.call(this, this.direction || 'down', false);
                } else {
                    this.handler.call(this, this.direction || 'down');
                }
            }
            this.isInActiveZone = false;
            
            // Reset hasTriggered when leaving intersection (for continuous: false)
            if (!this.continuous) {
                this.hasTriggered = false;
            }
        }
    }

    /**
     * Calculate scroll direction
     * @param {IntersectionObserverEntry|Object} entry - Entry with boundingClientRect, or object with boundingClientRect
     * @returns {string} 'up' | 'down' | 'left' | 'right'
     * @private
     */
    _getDirection(entry) {
        const rect = entry.boundingClientRect || entry;
        const rootBounds = entry.rootBounds;

        // Get current scroll position
        const currentScrollTop = this.context === window
            ? window.pageYOffset || document.documentElement.scrollTop
            : this.context.scrollTop;

        // Determine direction based on scroll position change
        // This is more reliable than using boundingClientRect alone
        if (currentScrollTop > this.lastScrollTop) {
            // Scrolling down
            return 'down';
        } else if (currentScrollTop < this.lastScrollTop) {
            // Scrolling up
            return 'up';
        }

        // If scroll position hasn't changed, use element position as fallback
        if (rootBounds) {
            // Element is above viewport = scrolling down to reach it
            if (rect.bottom < rootBounds.top) {
                return 'down';
            }
            // Element is below viewport = scrolling up to reach it
            if (rect.top > rootBounds.bottom) {
                return 'up';
            }
        }

        return 'down'; // Default
    }

    /**
     * Calculate rootMargin from Waypoints offset
     * @returns {string} CSS margin string (e.g., "100px 0px -200px 0px")
     * @private
     */
    _calculateRootMargin() {
        let offset = this.offset;

        // Handle function offset
        if (typeof offset === 'function') {
            offset = offset.call(this);
        }

        // Handle string aliases
        if (typeof offset === 'string') {
            offset = this._resolveStringOffset(offset);
        }

        // Convert to number
        const offsetNum = typeof offset === 'number' ? offset : parseFloat(offset) || 0;

        // Convert Waypoints offset to Intersection Observer rootMargin
        // Waypoints offset behavior:
        // - Positive offset (e.g., 300px): Trigger when element is 300px from top of viewport
        //   This means: trigger earlier (when element is further down)
        // - Negative offset (e.g., -100px): Trigger when element is 100px inside viewport
        //   This means: trigger later (when element is already visible)
        //
        // Intersection Observer rootMargin:
        // - Negative top margin: Expands intersection area upward = triggers earlier
        // - Positive bottom margin: Shrinks intersection area = triggers later
        // - Positive top margin: Shrinks intersection area = triggers later
        // - Negative bottom margin: Expands intersection area downward = triggers earlier
        
        if (offsetNum > 0) {
            // Positive offset: trigger earlier when element is further down
            // Expand intersection area upward by using negative top margin
            return `${-offsetNum}px 0px 0px 0px`;
        } else if (offsetNum < 0) {
            // Negative offset: trigger later when element is already visible
            // Shrink intersection area by using positive bottom margin
            return `0px 0px ${Math.abs(offsetNum)}px 0px`;
        }

        return '0px';
    }

    /**
     * Resolve string offset aliases
     * @param {string} offset - String offset like "bottom-in-view"
     * @returns {number}
     * @private
     */
    _resolveStringOffset(offset) {
        if (offset === 'bottom-in-view') {
            // Element bottom reaches viewport bottom
            const elementHeight = this.element.offsetHeight || this.element.clientHeight;
            const viewportHeight = this.context === window
                ? window.innerHeight
                : this.context.clientHeight;
            
            return viewportHeight - elementHeight;
        }

        // Add more string aliases as needed
        return 0;
    }

    /**
     * Destroy this waypoint instance
     * @returns {ScrollObserver} this
     */
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }

        // Remove scroll listener
        if (this.scrollHandler) {
            if (this.context === window) {
                window.removeEventListener('scroll', this.scrollHandler);
            } else {
                this.context.removeEventListener('scroll', this.scrollHandler);
            }
            this.scrollHandler = null;
        }

        const index = ScrollObserver.instances.indexOf(this);
        if (index > -1) {
            ScrollObserver.instances.splice(index, 1);
        }

        return this;
    }

    /**
     * Disable this waypoint
     * @returns {ScrollObserver} this
     */
    disable() {
        this.enabled = false;
        return this;
    }

    /**
     * Enable this waypoint
     * @returns {ScrollObserver} this
     */
    enable() {
        this.enabled = true;
        // Refresh to check current state
        if (this.observer && this.element) {
            // Trigger a manual check by re-observing
            this.observer.unobserve(this.element);
            this.observer.observe(this.element);
        }
        return this;
    }

    /**
     * Get next waypoint in group
     * @returns {ScrollObserver|null}
     */
    next() {
        const groupInstances = ScrollObserver.instances.filter(w => w.group === this.group);
        const currentIndex = groupInstances.indexOf(this);
        return currentIndex < groupInstances.length - 1 ? groupInstances[currentIndex + 1] : null;
    }

    /**
     * Get previous waypoint in group
     * @returns {ScrollObserver|null}
     */
    previous() {
        const groupInstances = ScrollObserver.instances.filter(w => w.group === this.group);
        const currentIndex = groupInstances.indexOf(this);
        return currentIndex > 0 ? groupInstances[currentIndex - 1] : null;
    }

    /**
     * Destroy all waypoint instances
     * @static
     */
    static destroyAll() {
        const instances = [...ScrollObserver.instances];
        instances.forEach(instance => instance.destroy());
    }

    /**
     * Disable all waypoint instances
     * @static
     */
    static disableAll() {
        ScrollObserver.instances.forEach(instance => instance.disable());
    }

    /**
     * Enable all waypoint instances
     * @static
     */
    static enableAll() {
        ScrollObserver.instances.forEach(instance => instance.enable());
    }

    /**
     * Refresh all waypoint instances
     * @static
     */
    static refreshAll() {
        // IntersectionObserver automatically refreshes, but we can force a check
        ScrollObserver.instances.forEach(instance => {
            if (instance.observer && instance.element) {
                // Re-observe to trigger check
                instance.observer.unobserve(instance.element);
                instance.observer.observe(instance.element);
            }
        });
    }

    /**
     * Get viewport height
     * @static
     * @returns {number}
     */
    static viewportHeight() {
        return window.innerHeight || document.documentElement.clientHeight;
    }

    /**
     * Get viewport width
     * @static
     * @returns {number}
     */
    static viewportWidth() {
        return document.documentElement.clientWidth;
    }
}

// Export as global Waypoint for compatibility
if (typeof window !== 'undefined') {
    window.Waypoint = ScrollObserver;
    window.ScrollObserver = ScrollObserver;
}

export default ScrollObserver;
